import { MAX_LEN_WEB_S, MIN_LEN_S } from "@goalhorns/shared";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin, { type Region, type RegionParams } from "wavesurfer.js/plugins/regions";
import { nudgeRegion } from "./region";

export interface RegionBounds {
  readonly start_ms: number;
  readonly end_ms: number;
}

export interface WaveformCallbacks {
  readonly onReady: (durationMs: number) => void;
  readonly onRegionChange: (bounds: RegionBounds) => void;
  readonly onPlayingChange: (playing: boolean) => void;
  readonly onError: () => void;
}

/** Imperative handle over one wavesurfer instance with a single playback region. */
export interface Waveform {
  setBounds(bounds: RegionBounds): void;
  setEditable(editable: boolean): void;
  togglePlay(): void;
  /** Names the region for assistive tech: its label and the section it covers. */
  annotate(label: string, valueText: string): void;
  /** The saved moment: a 360 ms fill from the left handle to the right. Skipped under reduced motion. */
  playFill(): void;
  destroy(): void;
}

interface Annotation {
  readonly label: string;
  readonly valueText: string;
}

/**
 * Unselected bars are ink-3 at 35% (hairline grey) on the base layer. The selected bars are the same
 * peaks drawn again in rubber ink on a second layer, clipped to the region bounds. Progress uses the
 * grey so the playhead never paints bars outside the section in ink. The region wash stays faint.
 */
const WAVE_COLOR = "rgba(94, 106, 118, 0.35)";
const PROGRESS_COLOR = WAVE_COLOR;
const INK_COLOR = "#121417";
const REGION_COLOR = "rgba(11, 61, 145, 0.1)";
const HIDDEN_CLIP = "inset(0 100% 0 0)";
const WAVE_HEIGHT_PX = 96;
const MS_PER_S = 1000;
const FILL_MS = 360;
const FILL_EASING = "cubic-bezier(0.16, 1, 0.3, 1)";
/** Wavesurfer's own handle is the drag target (44 px hit area); the visible 10 px line is in the overlay. */
const HANDLE_PX = 44;
const LINE_PX = 10;
const DOT_PX = 8;
/** Wavesurfer compares float region lengths, so the plugin gets a small tolerance. */
const LENGTH_TOLERANCE_S = 0.05;

const toSeconds = (ms: number) => ms / MS_PER_S;
const toMs = (seconds: number) => Math.round(seconds * MS_PER_S);

/**
 * Where a tap at `relativeX` (0..1 of the waveform width) puts a region of `lengthMs`: it starts
 * at the tap and is pulled back so it ends inside the song. A length longer than the song fills it.
 */
export function regionStartingAt(
  relativeX: number,
  durationMs: number,
  lengthMs: number,
): RegionBounds {
  const length = Math.min(lengthMs, durationMs);
  const fraction = Math.min(Math.max(relativeX, 0), 1);
  const start = Math.round(fraction * durationMs);
  const startMs = Math.max(0, Math.min(start, durationMs - length));
  return { start_ms: startMs, end_ms: startMs + length };
}

/**
 * Wavesurfer options for the single region. Only the end handle resizes, so a press on the start
 * edge drags the whole region.
 */
export function regionOptions(start: number, end: number, editable: boolean): RegionParams {
  return {
    start,
    end,
    color: REGION_COLOR,
    drag: editable,
    resize: editable,
    resizeStart: false,
    minLength: MIN_LEN_S - LENGTH_TOLERANCE_S,
    maxLength: MAX_LEN_WEB_S + LENGTH_TOLERANCE_S,
  };
}

function prefersReducedMotion(): boolean {
  const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  return query?.matches ?? false;
}

function element(styles: string): HTMLElement {
  const node = document.createElement("span");
  node.style.cssText = styles;
  return node;
}

/** Clip that keeps the part of a layer between two percentages of its width. */
function regionClip(leftPercent: number, rightPercent: number): string {
  return `inset(0 ${100 - rightPercent}% 0 ${leftPercent}%)`;
}

/** The selected bars: the base peaks drawn again in ink. Decorative and not interactive. */
function mountInkLayer(layer: HTMLElement, peaks: number[][], durationS: number): WaveSurfer {
  return WaveSurfer.create({
    container: layer,
    peaks,
    duration: durationS,
    height: WAVE_HEIGHT_PX,
    waveColor: INK_COLOR,
    progressColor: INK_COLOR,
    barWidth: 3,
    barGap: 2,
    barRadius: 2,
    normalize: true,
    interact: false,
    cursorWidth: 0,
  });
}

/** Blue line at `percent` of the waveform width, centred on it. Decorative. */
function lineAt(percent: number): HTMLElement {
  return element(
    `position:absolute;top:0;bottom:0;left:${percent}%;width:${LINE_PX}px;transform:translateX(-50%);background:var(--color-blue)`,
  );
}

/** Red faceoff dot on the centre line at `percent`. Decorative. */
function dotAt(percent: number): HTMLElement {
  return element(
    `position:absolute;top:50%;left:${percent}%;width:${DOT_PX}px;height:${DOT_PX}px;border-radius:9999px;background:var(--color-red);box-shadow:0 0 0 4px var(--color-board);transform:translate(-50%,-50%)`,
  );
}

/**
 * Touch and keyboard setup on the region element itself: attributes and listeners only, no child
 * nodes, so wavesurfer's own drag handling is untouched. `touch-action: none` stops a pan from
 * cancelling a drag; the start handle takes no pointer events so a press there drags the region.
 */
function prepareRegionElement(
  region: Region,
  onKey: (event: KeyboardEvent) => void,
  annotation: Annotation,
  editable: boolean,
): void {
  const node = region.element;
  if (!node) {
    return;
  }
  node.style.touchAction = "none";
  node.setAttribute("role", "slider");
  node.tabIndex = 0;
  node.onkeydown = onKey;
  node.setAttribute("aria-label", annotation.label);
  node.setAttribute("aria-valuetext", annotation.valueText);
  const startHandle = node.querySelector<HTMLElement>('[part~="region-handle-left"]');
  if (startHandle) {
    startHandle.style.pointerEvents = "none";
    startHandle.style.cursor = "grab";
  }
  const endHandle = node.querySelector<HTMLElement>('[part~="region-handle-right"]');
  if (endHandle) {
    endHandle.style.touchAction = "none";
    endHandle.style.width = `${HANDLE_PX}px`;
    endHandle.style.cursor = editable ? "ew-resize" : "default";
  }
}

/**
 * Mounts a waveform of `url` in `container` with one region. The region drag and resize limits
 * follow the web bounds; the caller still clamps the values it receives. The blue lines, red dots
 * and saved fill live in a sibling overlay after the container, never inside the region element.
 */
export function mountWaveform(
  container: HTMLElement,
  url: string,
  callbacks: WaveformCallbacks,
): Waveform {
  const ws = WaveSurfer.create({
    container,
    url,
    height: WAVE_HEIGHT_PX,
    waveColor: WAVE_COLOR,
    progressColor: PROGRESS_COLOR,
    barWidth: 3,
    barGap: 2,
    barRadius: 2,
    normalize: true,
  });
  const regions = ws.registerPlugin(RegionsPlugin.create());
  const inkLayer = document.createElement("div");
  inkLayer.setAttribute("aria-hidden", "true");
  inkLayer.style.cssText = `position:absolute;inset:0;pointer-events:none;clip-path:${HIDDEN_CLIP}`;
  container.after(inkLayer);
  const overlay = document.createElement("div");
  overlay.setAttribute("aria-hidden", "true");
  overlay.style.cssText = "position:absolute;inset:0;pointer-events:none";
  inkLayer.after(overlay);
  let ink: WaveSurfer | null = null;

  let region: Region | null = null;
  let editable = true;
  let playing = false;
  let lastStartMs: number | null = null;
  let annotation: Annotation = { label: "", valueText: "" };

  const currentEndMs = () => (region ? toMs(region.end) : null);

  /** Redraws the lines, dots and nothing else from the region's current bounds. */
  function drawOverlay(): void {
    const duration = ws.getDuration();
    if (!region || duration <= 0) {
      overlay.replaceChildren();
      inkLayer.style.clipPath = HIDDEN_CLIP;
      return;
    }
    const left = (region.start / duration) * 100;
    const right = (region.end / duration) * 100;
    overlay.replaceChildren(lineAt(left), lineAt(right), dotAt(left), dotAt(right));
    inkLayer.style.clipPath = regionClip(left, right);
  }

  function setBounds(bounds: RegionBounds): void {
    const start = toSeconds(bounds.start_ms);
    const end = toSeconds(bounds.end_ms);
    lastStartMs = bounds.start_ms;
    if (!region) {
      region = regions.addRegion(regionOptions(start, end, editable));
      prepareRegionElement(region, onKey, annotation, editable);
      drawOverlay();
      return;
    }
    if (toMs(region.start) !== bounds.start_ms || toMs(region.end) !== bounds.end_ms) {
      region.setOptions({ start, end });
      drawOverlay();
    }
  }

  /** Arrow keys move the section one second (five with Shift) and report it like a drag. */
  const onKey = (event: KeyboardEvent) => {
    if (!editable || !region) {
      return;
    }
    const lengthMs = toMs(region.end) - toMs(region.start);
    const next = nudgeRegion(
      toMs(region.start),
      lengthMs,
      toMs(ws.getDuration()),
      event.key,
      event.shiftKey,
    );
    if (!next || !next.ok) {
      return;
    }
    event.preventDefault();
    const bounds = { start_ms: next.start_ms, end_ms: next.end_ms };
    setBounds(bounds);
    callbacks.onRegionChange(bounds);
  };

  ws.on("ready", () => {
    ink?.destroy();
    ink = mountInkLayer(inkLayer, ws.exportPeaks(), ws.getDuration());
    callbacks.onReady(toMs(ws.getDuration()));
  });
  ws.on("error", () => callbacks.onError());
  ws.on("play", () => {
    playing = true;
    callbacks.onPlayingChange(true);
  });
  ws.on("pause", () => {
    playing = false;
    callbacks.onPlayingChange(false);
  });
  ws.on("timeupdate", (time: number) => {
    const endMs = currentEndMs();
    if (playing && endMs !== null && toMs(time) >= endMs) {
      ws.pause();
    }
  });

  regions.on("region-updated", (updated: Region) => {
    if (updated !== region) {
      return;
    }
    const bounds = { start_ms: toMs(updated.start), end_ms: toMs(updated.end) };
    if (playing && bounds.start_ms !== lastStartMs) {
      ws.setTime(updated.start);
    }
    lastStartMs = bounds.start_ms;
    drawOverlay();
    callbacks.onRegionChange(bounds);
  });

  /** A tap on the waveform moves the region so it starts there, keeping its length. */
  ws.on("click", (relativeX: number) => {
    if (!editable || !region) {
      return;
    }
    const lengthMs = toMs(region.end) - toMs(region.start);
    const bounds = regionStartingAt(relativeX, toMs(ws.getDuration()), lengthMs);
    setBounds(bounds);
    callbacks.onRegionChange(bounds);
  });

  function togglePlay(): void {
    if (playing) {
      ws.pause();
      return;
    }
    if (region) {
      const time = ws.getCurrentTime();
      if (time < region.start || time >= region.end) {
        ws.setTime(region.start);
      }
    }
    void ws.play();
  }

  return {
    setBounds,
    setEditable(next: boolean) {
      editable = next;
      if (region) {
        region.setOptions({ drag: next, resize: next });
        prepareRegionElement(region, onKey, annotation, editable);
      }
    },
    togglePlay,
    annotate(label: string, valueText: string) {
      annotation = { label, valueText };
      if (region) {
        prepareRegionElement(region, onKey, annotation, editable);
      }
    },
    playFill() {
      const duration = ws.getDuration();
      if (!region || duration <= 0 || prefersReducedMotion()) {
        return;
      }
      // The section's ink wipes in from the left blue line; the finished state is the filled section.
      const left = (region.start / duration) * 100;
      const right = (region.end / duration) * 100;
      inkLayer.animate(
        [{ clipPath: regionClip(left, left) }, { clipPath: regionClip(left, right) }],
        { duration: FILL_MS, easing: FILL_EASING },
      );
    },
    destroy() {
      overlay.remove();
      inkLayer.remove();
      ink?.destroy();
      ws.destroy();
    },
  };
}
